import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const FEES_ACCESS_KEY = "asha_os_fees_1loc_fellows_access";

const STYLES = `
.pms-quote-doc {
  --paper: #fcfcfd;
  --paper-2: #f4f5f8;
  --ink: #1c2540;
  --ink-2: #404a66;
  --ink-3: #737d96;
  --line: #d8dde6;
  --line-2: #e7eaf0;
  --accent: #3a5bd9;
  --accent-deep: #2638b2;
  --accent-soft: #eef0fa;
  --accent-ink: #2c43c4;
  --accent-soft-2: #eaedf7;
  --accent-border: #bdc6e8;

  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-feature-settings: 'ss01', 'cv11';
}

.pms-quote-doc *,
.pms-quote-doc *::before,
.pms-quote-doc *::after { box-sizing: border-box; }

.pms-quote-doc .page {
  max-width: 1040px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.02),
    0 30px 60px -30px rgba(20,30,40,0.18),
    0 12px 24px -16px rgba(20,30,40,0.10);
  overflow: hidden;
}

.pms-quote-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.pms-quote-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .brand-mark {
  width: 28px; height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}
.pms-quote-doc .brand-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.pms-quote-doc .brand-meta {
  color: var(--ink-3);
  font-weight: 450;
}
.pms-quote-doc .topbar-right {
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
}
.pms-quote-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.pms-quote-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(238, 240, 250, 0.7);
}

.pms-quote-doc .hero {
  padding: 72px 56px 56px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(1200px 320px at 90% -100px, rgba(216, 222, 248, 0.55), transparent 60%),
    var(--paper);
}
.pms-quote-doc .eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.pms-quote-doc .eyebrow b { color: var(--accent-ink); font-weight: 500; }
.pms-quote-doc h1.title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.035em;
  margin: 0 0 14px;
  color: var(--ink);
  max-width: 18ch;
}
.pms-quote-doc .title-sub {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 24px;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
  margin: 0 0 26px;
}
.pms-quote-doc .lede {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 62ch;
  margin: 0 0 48px;
  text-wrap: pretty;
}

.pms-quote-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  margin-top: 8px;
}
.pms-quote-doc .meta-foot {
  margin: 14px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-3);
  font-style: italic;
}
.pms-quote-doc .meta-cell {
  padding: 18px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .meta-cell:first-child { padding-right: 36px; }
.pms-quote-doc .meta-cell:not(:first-child) { padding-left: 36px; padding-right: 36px; }
.pms-quote-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.pms-quote-doc .meta-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
}
.pms-quote-doc .meta-value {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .meta-value.placeholder {
  color: var(--ink-3);
  font-weight: 400;
}

.pms-quote-doc section.s {
  padding: 64px 56px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc section.s:last-of-type { border-bottom: 0; }
.pms-quote-doc .s-head {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  margin-bottom: 36px;
  align-items: baseline;
}
.pms-quote-doc .s-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding-top: 10px;
  border-top: 1px solid var(--ink);
  width: 52px;
}
.pms-quote-doc .s-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 34px;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .s-sub {
  font-size: 15px;
  color: var(--ink-2);
  margin-top: 10px;
  max-width: 60ch;
  text-wrap: pretty;
}
.pms-quote-doc .s-sub b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .s-body {
  margin-left: 152px;
}

.pms-quote-doc .contingency-note {
  padding: 22px 26px;
  margin: 0 0 32px;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
}
.pms-quote-doc .contingency-note p {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--ink-2);
}
.pms-quote-doc .contingency-note p + p {
  margin-top: 10px;
}
.pms-quote-doc .contingency-note b {
  color: var(--ink);
  font-weight: 500;
}

.pms-quote-doc .incl-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 0;
  border-top: 1px solid var(--line);
}
.pms-quote-doc .incl-col {
  padding: 28px 32px 28px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .incl-col + .incl-col {
  padding-left: 32px;
  padding-right: 0;
  border-right: 0;
}
.pms-quote-doc .incl-col-head {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 2px;
  align-items: center;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc .incl-count {
  grid-row: 1 / span 2;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 38px;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--ink);
}
.pms-quote-doc .incl-count.accent { color: var(--accent); }
.pms-quote-doc .incl-col-title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
  align-self: end;
}
.pms-quote-doc .incl-col-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  align-self: start;
}

.pms-quote-doc .prod-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pms-quote-doc .prod-list li {
  padding-bottom: 14px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .prod-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .prod-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pms-quote-doc .prod-sub {
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.5;
}
.pms-quote-doc .soon {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
}

.pms-quote-doc .agent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  counter-reset: agent;
}
.pms-quote-doc .agent-list li {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 13.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  padding: 11px 14px 11px 28px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: agent;
}
.pms-quote-doc .agent-list li::before {
  content: counter(agent, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--ink-3);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.pms-quote-doc .agent-list li:nth-last-child(-n+2) { border-bottom: 0; }
.pms-quote-doc .agent-list li:nth-child(odd) { padding-right: 18px; }

.pms-quote-doc .option-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 36px;
}
.pms-quote-doc .opt-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 30px 28px;
  background: var(--paper);
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}
.pms-quote-doc .opt-card.accent {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
  border-color: var(--accent-border);
}
.pms-quote-doc .opt-tag {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pms-quote-doc .opt-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.015em;
  color: var(--ink);
}
.pms-quote-doc .opt-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 46px;
  letter-spacing: -0.03em;
  color: var(--ink);
  line-height: 1;
  margin-top: 4px;
}
.pms-quote-doc .opt-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 8px;
  letter-spacing: 0;
}
.pms-quote-doc .opt-desc {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.55;
  margin-top: 4px;
}
.pms-quote-doc .opt-desc b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .opt-includes {
  list-style: none;
  margin: 12px 0 0;
  padding: 14px 0 0;
  border-top: 1px solid var(--line-2);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pms-quote-doc .opt-includes li {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.5;
  padding-left: 18px;
  position: relative;
}
.pms-quote-doc .opt-includes li::before {
  content: "✓";
  position: absolute;
  left: 0;
  top: 0;
  color: var(--accent-ink);
  font-weight: 600;
  font-size: 11px;
}
.pms-quote-doc .opt-includes li b { color: var(--ink); font-weight: 500; }

.pms-quote-doc .piecemeal-line {
  font-family: 'Inter', sans-serif;
  font-size: 19px;
  line-height: 1.6;
  color: var(--ink-2);
  margin: 0 0 22px;
}
.pms-quote-doc .piecemeal-line b {
  color: var(--ink);
  font-weight: 600;
}
.pms-quote-doc .piecemeal-line .piecemeal-num {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
}

.pms-quote-doc .see-comparison {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 26px;
  margin: 24px 0 0;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
}
.pms-quote-doc .see-comparison-text {
  font-family: 'Inter Tight', sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.45;
  margin: 0;
  flex: 1 1 auto;
  min-width: 240px;
}
.pms-quote-doc .see-comparison-text span {
  display: block;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-3);
  letter-spacing: 0;
}
.pms-quote-doc .see-comparison-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  background: var(--accent);
  color: #fff;
  font-family: 'Inter Tight', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.005em;
  text-decoration: none;
  border: 1px solid var(--accent-deep);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.15) inset,
    0 2px 6px -2px rgba(38, 56, 178, 0.35);
  transition: background 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
  white-space: nowrap;
}
.pms-quote-doc .see-comparison-btn:hover {
  background: var(--accent-deep);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.15) inset,
    0 4px 10px -2px rgba(38, 56, 178, 0.45);
}
.pms-quote-doc .see-comparison-btn:active {
  transform: translateY(1px);
}
.pms-quote-doc .see-comparison-btn .arrow {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 400;
  transition: transform 0.15s ease;
}
.pms-quote-doc .see-comparison-btn:hover .arrow {
  transform: translateX(2px);
}

.pms-quote-doc .concessions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid var(--line);
  counter-reset: concession;
}
.pms-quote-doc .concessions-list li {
  padding: 24px 0 24px 52px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: concession;
}
.pms-quote-doc .concessions-list li:last-child { border-bottom: 0; }
.pms-quote-doc .concessions-list li::before {
  content: counter(concession, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 26px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  color: var(--accent-ink);
  font-weight: 500;
}
.pms-quote-doc .concession-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 19px;
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: var(--ink);
  margin-bottom: 8px;
}
.pms-quote-doc .concession-desc {
  font-size: 14px;
  color: var(--ink-2);
  line-height: 1.6;
  max-width: 78ch;
  text-wrap: pretty;
}
.pms-quote-doc .concession-desc b { color: var(--ink); font-weight: 500; }

.pms-quote-doc .fineprint {
  list-style: none;
  padding: 22px 0 0;
  margin: 32px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid var(--line-2);
}
.pms-quote-doc .fineprint li {
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.6;
  max-width: 88ch;
  padding-left: 18px;
  position: relative;
}
.pms-quote-doc .fineprint li::before {
  content: "";
  position: absolute;
  left: 0; top: 9px;
  width: 4px; height: 4px;
  border-radius: 99px;
  background: var(--ink-3);
}
.pms-quote-doc .fineprint .fp-label {
  color: var(--ink);
  font-weight: 500;
  font-family: 'Inter Tight', sans-serif;
  margin-right: 4px;
}
.pms-quote-doc .fineprint b { color: var(--ink); font-weight: 500; }

.pms-quote-doc table.lines {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc table.lines th,
.pms-quote-doc table.lines td {
  text-align: left;
  padding: 22px 0;
  vertical-align: top;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc table.lines thead th {
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc table.lines tbody tr:last-child td { border-bottom: 0; }
.pms-quote-doc .li-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 6px;
}
.pms-quote-doc .li-desc {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 52ch;
}
.pms-quote-doc .li-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .li-amount.strike {
  text-decoration: line-through;
  text-decoration-color: var(--ink-3);
  color: var(--ink-3);
}
.pms-quote-doc .li-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .li-amount .waived-badge {
  display: inline-block;
  margin-top: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-ink);
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  padding: 3px 8px;
  border-radius: 4px;
}
.pms-quote-doc .li-note {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.5;
}
.pms-quote-doc .col-item { width: 40%; padding-right: 32px !important; }
.pms-quote-doc .col-billing { width: 26%; padding-right: 32px !important; }
.pms-quote-doc .col-amount { width: 22%; text-align: right !important; padding-right: 32px !important; }
.pms-quote-doc .col-notes { width: 34%; padding-left: 0 !important; }
.pms-quote-doc td.col-amount,
.pms-quote-doc th.col-amount { text-align: right !important; }

.pms-quote-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
}

/* FAQ section */
.pms-quote-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  border-top: 1px solid var(--line);
  padding-top: 28px;
}
.pms-quote-doc .faq-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  padding-bottom: 22px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .faq-q {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 18px;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .faq-a {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
  max-width: 80ch;
  margin: 0;
}
.pms-quote-doc .faq-a b { color: var(--ink); font-weight: 500; }

@media (max-width: 900px) {
  .pms-quote-doc .page { margin: 24px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail { padding-left: 28px; padding-right: 28px; }
  .pms-quote-doc h1.title { font-size: 44px; }
  .pms-quote-doc .s-head { grid-template-columns: 1fr; gap: 12px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .pms-quote-doc .incl-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .incl-col { padding: 24px 0; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .option-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .contingency-note { padding: 18px 20px; margin-bottom: 24px; }
}

@media (max-width: 720px) {
  .pms-quote-doc .page { margin: 14px; border-radius: 14px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail {
    padding-left: 20px; padding-right: 20px;
  }
  .pms-quote-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .pms-quote-doc .topbar-right { gap: 8px; }
  .pms-quote-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .pms-quote-doc section.s { padding-top: 40px; padding-bottom: 40px; }

  .pms-quote-doc h1.title { font-size: 34px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 18px; margin-bottom: 20px; }
  .pms-quote-doc .lede { font-size: 15px; margin-bottom: 32px; }

  .pms-quote-doc .meta-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .meta-cell {
    padding: 14px 0 !important;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .meta-cell:last-child { border-bottom: 0; }

  .pms-quote-doc .opt-amount { font-size: 38px; }

  .pms-quote-doc table.lines,
  .pms-quote-doc table.lines tbody,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc table.lines td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc table.lines thead { display: none; }
  .pms-quote-doc table.lines tr {
    padding: 22px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc table.lines tbody tr:first-child { padding-top: 18px; }
  .pms-quote-doc table.lines tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc table.lines td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc table.lines td + td { margin-top: 14px; }
  .pms-quote-doc table.lines td.col-amount { text-align: left !important; }
  .pms-quote-doc .li-desc,
  .pms-quote-doc .li-note { max-width: none; }

  .pms-quote-doc .concessions-list li {
    padding: 22px 0 22px 38px;
  }
  .pms-quote-doc .concessions-list li::before { top: 24px; }
  .pms-quote-doc .concession-name { font-size: 17px; }
  .pms-quote-doc .concession-desc { font-size: 13.5px; }

  .pms-quote-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }

}

@media print {
  @page {
    size: letter;
    margin: 0.3in;
  }
  html, body {
    background: #fff !important;
  }
  body.pms-quote-body { background: #fff !important; }
  .pms-quote-doc { font-size: 11px !important; }
  .pms-quote-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .pms-quote-doc .topbar { padding: 14px 24px; }
  .pms-quote-doc .hero { padding: 32px 24px 24px; }
  .pms-quote-doc h1.title { font-size: 32px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 16px; margin-bottom: 14px; }
  .pms-quote-doc .lede { margin-bottom: 20px; font-size: 12px; }
  .pms-quote-doc section.s { padding: 24px; }
  .pms-quote-doc .s-head {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }
  .pms-quote-doc .s-title { font-size: 22px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .tail { padding: 12px 24px; }

  .pms-quote-doc .meta-grid { grid-template-columns: repeat(2, 1fr); }
  .pms-quote-doc .incl-grid { display: block; }
  .pms-quote-doc .incl-col {
    padding: 18px 0;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .incl-col:last-child { border-bottom: 0; }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .agent-list { grid-template-columns: 1fr 1fr 1fr; }
  .pms-quote-doc .option-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
  .pms-quote-doc .opt-card { padding: 18px 18px; }
  .pms-quote-doc .opt-amount { font-size: 30px; }

  .pms-quote-doc .meta-grid,
  .pms-quote-doc .opt-card,
  .pms-quote-doc .contingency-note,
  .pms-quote-doc .see-comparison,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc .fineprint li,
  .pms-quote-doc .prod-list li,
  .pms-quote-doc .agent-list li,
  .pms-quote-doc .concessions-list li {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .pms-quote-doc h1.title,
  .pms-quote-doc .s-title,
  .pms-quote-doc .prod-name,
  .pms-quote-doc .li-name,
  .pms-quote-doc .incl-col-head,
  .pms-quote-doc .s-head {
    break-after: avoid;
    page-break-after: avoid;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const OsFees1LocPage = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (hasStoredContentAccess(FEES_ACCESS_KEY)) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FEES_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Fee schedule access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";

    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "";

    const link3 = document.createElement("link");
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&family=Fraunces:opsz,wght@9..144,300;9..144,400&display=swap";

    document.head.appendChild(link1);
    document.head.appendChild(link2);
    document.head.appendChild(link3);

    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#f1efe7";
    document.body.style.margin = "0";
    document.body.classList.add("pms-quote-body");
    document.title = "Asha — AI Innovation Fellows Fee Schedule";

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("pms-quote-body");
    };
  }, []);

  return (
    <div className="pms-quote-doc">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {isLocked && <EmailGate onAccessGranted={handleAccessGranted} />}

      <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <div className="topbar-right">
            <span className="pill">AI Innovation Fellows</span>
            <span className="pill">Confidential</span>
          </div>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Fee Schedule</b> &nbsp;·&nbsp; Asha AI Operating System &nbsp;·&nbsp; <b>AI Innovation Fellows</b> Pricing
          </div>
          <h1 className="title">
            The AI Operating System for Modern Dental Offices
          </h1>
          <div className="title-sub">
            Single-location quote — AI Innovation Fellows pricing on a 12-month engagement
          </div>
          <p className="lede">
            This fee schedule outlines{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>AI Innovation
            Fellows</b> pricing for a{" "}
            <b style={{ color: "var(--ink)", fontWeight: 500 }}>single
            location</b> on the Asha platform, with a 12-month engagement.
            Two engagement models are available, with a single all-in flat
            monthly fee per location. Final terms are governed by the
            executed Statement of Work and the Asha Terms of Service.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">Asha Health, Inc.</div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Quote valid until</div>
              <div className="meta-value">May 26th, 2026</div>
            </div>
          </div>
          <p className="meta-foot">
            Pricing in this quote is contingent on executing a 12-month
            engagement. This quote expires on May 26th, 2026.
          </p>
        </div>

        {/* § 01 — INCLUDED */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 01</div>
            <div>
              <h2 className="s-title">What's included</h2>
              <p className="s-sub">
                Your location ships with our full AI agent suite plus the
                Asha software layer. The exact software footprint depends on
                which engagement model you select in §03 — the{" "}
                <b>Continuous Integration</b> model layers Asha on top of
                your existing PMS, while the <b>PMS</b> model replaces your
                existing practice management system with Asha's PMS.
              </p>
            </div>
          </div>
          <div className="s-body">
            <div className="incl-grid">
              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count">10</span>
                  <span className="incl-col-title">Softwares</span>
                  <span className="incl-col-sub">
                    Full Asha platform — included under both the Continuous
                    Integration and PMS engagement models.
                  </span>
                </div>
                <ul className="prod-list">
                  <li>
                    <div className="prod-name">
                      Practice Management System{" "}
                      <span className="soon">PMS engagement only</span>
                    </div>
                    <div className="prod-sub">
                      Patient charts · Perio charting · Ledgers &amp; billing ·
                      Treatment planning · Scheduling · Clinical notes ·
                      Cloud-based · Multi-user access
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Communication Suite</div>
                    <div className="prod-sub">
                      VoIP phone system · Yealink desk phones · 2-way texting ·
                      Universal inbox · Team chat &amp; channels · Smart voice
                      logs · Intelligent fax · Next-gen digital forms
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Marketing Suite</div>
                    <div className="prod-sub">
                      SMS &amp; email campaigns · Automated workflows ·
                      Clinically-aware segmentation · AI handles patient
                      replies
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">eClaims Portal</div>
                    <div className="prod-sub">
                      Built-in eClaims · Clearinghouse connectivity · Single
                      login · Integrated submission
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Revenue Intelligence Suite</div>
                    <div className="prod-sub">
                      Reporting &amp; analytics dashboards · KPI tracking ·
                      Collection-rate insights · Revenue cycle analytics ·
                      Performance metrics
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      Insurance Verification Software
                    </div>
                    <div className="prod-sub">
                      Automated verification · Real-time eligibility · Benefits
                      breakdown upload · Dual-source verification
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Integrated Payments Suite</div>
                    <div className="prod-sub">
                      Text-to-pay · Card readers · Multi-method support ·
                      Automated reminders
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Patient Portal</div>
                    <div className="prod-sub">
                      Secure patient links · Interactive forms · Treatment-plan
                      viewing · Insurance info upload
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      ePrescribe{" "}
                      <span className="soon">PMS engagement · Coming soon</span>
                    </div>
                    <div className="prod-sub">
                      Electronic prescriptions · Integrated with PMS
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Online Scheduling</div>
                    <div className="prod-sub">
                      Patient self-scheduling · Real-time availability
                    </div>
                  </li>
                </ul>
              </div>

              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count accent">21</span>
                  <span className="incl-col-title">AI Products</span>
                  <span className="incl-col-sub">
                    Front-office, back-office, and clinical &amp; intelligence
                    AI — end-to-end.
                  </span>
                </div>
                <ul className="agent-list">
                  <li>AI Receptionist</li>
                  <li>AI Schedule Optimizer</li>
                  <li>AI Hygiene Recall</li>
                  <li>AI Patient Experience</li>
                  <li>AI Case Acceptance</li>
                  <li>AI Patient Intake</li>
                  <li>AI Practice Manager</li>
                  <li>AI Fax Manager</li>
                  <li>AI Insurance Verification</li>
                  <li>AI Claim Submission</li>
                  <li>AI Claim Scrubbing</li>
                  <li>AI Denial Management</li>
                  <li>AI Patient Billing</li>
                  <li>AI AR &amp; Collections</li>
                  <li>AI EOB Posting</li>
                  <li>AI Insurance Calls</li>
                  <li>Asha Copilot</li>
                  <li>AI Scribe</li>
                  <li>Voice Perio</li>
                  <li>AI Treatment Planner</li>
                  <li>AI Revenue Analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* § 02 — PRICING COMPARISON */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 02</div>
            <div>
              <h2 className="s-title">Pricing comparison</h2>
              <p className="s-sub">
                How Asha's all-in fee compares to assembling the same scope
                from other vendors at their actual published rates.
              </p>
            </div>
          </div>

          <div className="s-body">
            <p className="piecemeal-line">
              Asha's offering is so big that{" "}
              <b>
                piecemealing the same capabilities from other vendors would
                cost{" "}
                <span className="piecemeal-num">~$11,000 / month</span> +{" "}
                <span className="piecemeal-num">3.5% of collections</span>
              </b>{" "}
              per location — based on those vendors' actual published rack
              rates.
            </p>
            <div className="see-comparison">
              <p className="see-comparison-text">
                Don't believe it?
                <span>
                  See the full feature-by-feature breakdown.
                </span>
              </p>
              <a
                href="/os-fees-comparison"
                target="_blank"
                rel="noopener noreferrer"
                className="see-comparison-btn"
              >
                View pricing comparison
                <span className="arrow">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* § 03 — OUR PRICING */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 03</div>
            <div>
              <h2 className="s-title">Our pricing for you</h2>
              <p className="s-sub">
                Under this quote, you pay a single all-in flat monthly fee
                for your location. Choose the engagement model that fits
                your practice.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="contingency-note">
              <p>
                <b>
                  This AI Innovation Fellows pricing is contingent on a
                  12-month engagement for the location.
                </b>{" "}
                The flat all-in monthly rate per location shown below is
                offered in exchange for executing a Statement of Work with a
                12-month term.
              </p>
              <p>
                See <b>§ 05 Special concessions</b> below for a 3-month
                opt-out clause that allows the practice to exit the
                engagement for any reason within the first 90 days.
              </p>
            </div>

            <div className="option-grid">
              <div className="opt-card">
                <div className="opt-tag">Option A</div>
                <div className="opt-name">Continuous Integration</div>
                <div className="opt-amount">
                  $2,500<small>/ location / month · all-in</small>
                </div>
                <div className="opt-desc">
                  Asha layers on top of your existing PMS via continuous
                  integration. <b>Your PMS and ePrescribe stay where they
                  are.</b> Best fit if your practice runs on Dentrix,
                  Eaglesoft, or Open Dental and wants the full Asha AI &amp;
                  software stack alongside it.
                </div>
                <ul className="opt-includes">
                  <li><b>Full Asha software stack</b> layered on top of your existing PMS</li>
                  <li><b>21 AI products</b> across front-office &amp; back-office</li>
                  <li>PMS Integration &amp; maintenance — <b>included</b></li>
                </ul>
              </div>
              <div className="opt-card accent">
                <div className="opt-tag">Option B</div>
                <div className="opt-name">PMS</div>
                <div className="opt-amount">
                  $2,000<small>/ location / month · all-in</small>
                </div>
                <div className="opt-desc">
                  Asha's AI-Native PMS takes the place of your existing
                  practice management system — <b>one platform end-to-end,
                  no stitching.</b> Best fit if your practice is open to
                  migrating off legacy PMS software onto a cloud-native,
                  AI-native PMS.
                </div>
                <ul className="opt-includes">
                  <li><b>10 softwares</b> unified in one platform (incl. the Asha PMS)</li>
                  <li><b>21 AI products</b> across front-office &amp; back-office</li>
                  <li>ePrescribe (coming soon) — <b>included</b></li>
                </ul>
              </div>
            </div>

            <ul className="fineprint">
              <li>
                <span className="fp-label">All-in Flat Rate.</span>
                The rate above is a single all-in monthly fee for your
                location. The PMS Integration Fee under Continuous
                Integration is included.
              </li>
              <li>
                <span className="fp-label">12-Month Engagement.</span>
                Pricing in this quote is contingent on executing a Statement
                of Work with a <b>12-month term</b>. If the practice prefers
                a shorter term, the standard fee schedule applies and the
                practice will be re-quoted accordingly.
              </li>
              <li>
                <span className="fp-label">Monthly Retainer &amp; Billing.</span>
                On the first day of each billing month, the Practice pays
                the flat all-in fee — <b>$2,500</b> under Continuous
                Integration or <b>$2,000</b> under PMS. Billed per location,
                in advance.
              </li>
              <li>
                <span className="fp-label">AI Credits.</span>
                100,000 credits per location are included each month. We
                specify AI credit limits because running AI models is
                expensive and uncapped usage would risk us losing money on
                contracts. Overages bill at{" "}
                <b>$0.005 per additional credit</b>. Your account manager
                can share utilization-management techniques &amp; tools to
                help your team stay within the included allotment.
              </li>
              <li>
                <span className="fp-label">Contract Term.</span>
                Engagement is a <b>12-month term</b> per location. Renewals
                are month-to-month thereafter unless a new term is executed.
                See <b>§ 05 Special concessions</b> for the 3-month opt-out
                clause.
              </li>
            </ul>
          </div>
        </section>

        {/* § 04 — ONE-TIME */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 04</div>
            <div>
              <h2 className="s-title">One-time fees &amp; deposits</h2>
              <p className="s-sub">
                Charged once per location at signing. Under this quote, the
                Setup &amp; Premium Onboarding Fee is <b>waived</b>.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Item</th>
                  <th className="col-amount">Amount</th>
                  <th className="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">
                      Setup &amp; Premium Onboarding
                    </div>
                    <div className="li-desc">
                      Implementation build-out, configuration, training, and
                      go-live support — delivered by a dedicated
                      implementation team.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount strike">
                      $1,800<small>one-time / location</small>
                    </div>
                    <span className="waived-badge">Waived</span>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      <b>Waived</b> under this quote. Standard onboarding
                      included at no cost.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Deposit</div>
                    <div className="li-desc">
                      Equal to one Monthly Retainer at the selected
                      engagement model's flat rate. Secures your onboarding
                      start date and is applied to the final month's invoice
                      when the engagement ends.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $2,500 / $2,000<small>per location · CI / PMS</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Collected upon signing. Refundable in the form of a
                      credit against the final month's invoice.
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* § 05 — SPECIAL CONCESSIONS */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 05</div>
            <div>
              <h2 className="s-title">Special concessions</h2>
              <p className="s-sub">
                Concessions offered as part of this quote, in addition to
                Asha's standard Statement of Work terms.
              </p>
            </div>
          </div>

          <div className="s-body">
            <ul className="concessions-list">
              <li>
                <div className="concession-name">
                  3-Month Opt-Out Clause
                </div>
                <div className="concession-desc">
                  The Practice may, at its sole discretion and for any
                  reason, terminate this engagement within the first{" "}
                  <b>ninety (90) days</b> from go-live. To exercise this
                  opt-out, the Practice provides written notice to Asha
                  before the end of the 90-day window. Upon exercise, the
                  Practice is released from the remainder of the 12-month
                  term with no early-termination penalty; monthly fees
                  through the effective opt-out date remain payable, and the
                  Deposit is applied against the final month's invoice.
                </div>
              </li>
            </ul>
          </div>
        </section>

        {/* § 06 — FAQ */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 06</div>
            <div>
              <h2 className="s-title">Frequently asked questions</h2>
              <p className="s-sub">
                Quick answers to the most common questions about how this
                quote applies to your practice.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="faq-list">
              <div className="faq-item">
                <h3 className="faq-q">
                  How does the 3-month opt-out clause work?
                </h3>
                <p className="faq-a">
                  Within the first <b>90 days</b> from go-live, the Practice
                  may terminate the engagement for any reason by providing
                  written notice to Asha. The Practice is released from the
                  rest of the 12-month term with no early-termination
                  penalty. Monthly fees through the effective opt-out date
                  remain payable; the Deposit is applied against the final
                  month's invoice. See <b>§ 05 Special concessions</b> for
                  the full clause.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What happens after the 12-month term ends?
                </h3>
                <p className="faq-a">
                  Renewals are <b>month-to-month</b> thereafter at the rate
                  in effect at renewal time, unless a new term is executed.
                  Either party may end a month-to-month renewal with
                  30 days' notice.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What's the difference between the two engagement models?
                </h3>
                <p className="faq-a">
                  Under <b>Continuous Integration</b> (CI), Asha layers on
                  top of your existing PMS — your PMS &amp; ePrescribe stay
                  where they are, and Asha continuously integrates with
                  them. Under the <b>PMS</b> engagement, Asha's AI-Native
                  PMS takes the place of your existing practice management
                  system, giving you one unified platform end-to-end. Both
                  options ship with the complete suite of 21 AI products.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  Which PMS systems does Continuous Integration support?
                </h3>
                <p className="faq-a">
                  The Continuous Integration model supports{" "}
                  <b>Dentrix</b>, <b>Eaglesoft</b>, and <b>Open Dental</b>.
                  Other PMS systems are evaluated on a case-by-case basis.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  Is there a Setup &amp; Premium Onboarding Fee?
                </h3>
                <p className="faq-a">
                  The standard <b>$1,800</b> one-time Setup &amp; Premium
                  Onboarding Fee is <b>waived</b> under this quote — Asha
                  covers the dedicated implementation team at no cost to the
                  Practice. The <b>Deposit</b> is refundable in the form of
                  a credit against the final month's invoice.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>
            Confidential — Summary of proposed commercial terms; not a
            binding agreement.
          </span>
          <span>Page 01 / 01</span>
        </div>
      </div>

    </div>
  );
};

export default OsFees1LocPage;
